import { useState } from 'react'
import { useAtom } from 'jotai'
import { isEmpty } from 'lodash-es'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { logSentryError } from '@node-space/utils'
import { useCreateContactBankAccountMutation } from 'hooks/mutations/Contacts/useCreateContactBankAccount'
import { useCreateContactBankAccount2FAMutation } from 'hooks/mutations/Contacts/useCreateContactBankAccount2FAMutation'
import { useTwoFAContext } from 'hooks/useTwoFAContext'
import { contactActionAtom } from 'pages/Contacts/atoms/contactAtoms'
import {
  ContactFiatInstrumentFormData,
  ContactFiatInstrumentFormKeys,
} from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { BaseErrorResponse, BeneficiaryTransferDestination } from 'types/beneficiaries'
import { Contact, CreateContactBankAccountRequest, InstrumentType } from 'types/contacts'
import { TwoFAAuthRequest } from 'types/generics'

interface UseCreateContactBankAccountProps {
  contact?: Contact
  formValues?: ContactFiatInstrumentFormData
  form?: UseFormReturn<ContactFiatInstrumentFormData>
  onSubmit2FASuccess: () => void
}

export const useCreateContactBankAccount = ({
  contact,
  formValues,
  form,
  onSubmit2FASuccess,
}: UseCreateContactBankAccountProps) => {
  const { t } = useTranslation()
  const { twoFactorEnabled } = useTwoFAContext()
  const [contactsAction] = useAtom(contactActionAtom)

  const [calloutError, setCalloutError] = useState<string | null>(null)

  const {
    mutate: submitBankAccount,
    isPending: isSubmittingBankAccount,
    error: submitBankAccountError,
  } = useCreateContactBankAccountMutation()

  const { mutate: submit2FA, isPending: isSubmitting2FA } = useCreateContactBankAccount2FAMutation()

  const getRequestBankPayload = (formValues: ContactFiatInstrumentFormData) => {
    const {
      bankAddressLine1,
      bankAddressLine2,
      bankCity,
      bankRegion,
      bankPostCode,
      bankCountryCode,
      bankName,
    } = formValues ?? {}

    const hasBankAddress =
      !!bankAddressLine1 ||
      !!bankAddressLine2 ||
      !!bankCity ||
      !!bankRegion ||
      !!bankPostCode ||
      !!bankCountryCode

    return {
      ...((bankName || hasBankAddress) && {
        bank: {
          ...(formValues?.bankName && {
            name: formValues.bankName,
          }),
          ...(hasBankAddress && {
            address: {
              addressLine1: !isEmpty(formValues?.bankAddressLine1)
                ? formValues?.bankAddressLine1
                : null,
              addressLine2: !isEmpty(formValues?.bankAddressLine2)
                ? formValues?.bankAddressLine2
                : null,
              city: !isEmpty(formValues?.bankCity) ? formValues.bankCity : null,
              region: !isEmpty(formValues?.bankRegion) ? formValues.bankRegion : null,
              postCode: !isEmpty(formValues?.bankPostCode) ? formValues.bankPostCode : null,
              country: !isEmpty(formValues?.bankCountryCode) ? formValues.bankCountryCode : null,
            },
          }),
        },
      }),
    }
  }

  const getRequestIntermediaryBankPayload = (formValues: ContactFiatInstrumentFormData) => {
    const { transferDestination, intermediaryBankCode } = formValues ?? {}
    const isSWIFT = transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
    const hasIntermediaryBank = isSWIFT && intermediaryBankCode

    return {
      ...(hasIntermediaryBank && {
        intermediaryBank: {
          bankName: formValues?.intermediaryBankName || '',
          code: formValues?.intermediaryBankCode,
          ...(formValues?.intermediaryBankRoutingNumber && {
            routingNumber: formValues.intermediaryBankRoutingNumber,
          }),
          address: {
            addressLine1: !isEmpty(formValues?.intermediaryBankAddressLine1)
              ? formValues.intermediaryBankAddressLine1
              : null,
            addressLine2: !isEmpty(formValues?.intermediaryBankAddressLine2)
              ? formValues.intermediaryBankAddressLine2
              : null,
            city: !isEmpty(formValues?.intermediaryBankCity)
              ? formValues.intermediaryBankCity
              : null,
            region: !isEmpty(formValues?.intermediaryBankRegion)
              ? formValues.intermediaryBankRegion
              : null,
            postCode: !isEmpty(formValues?.intermediaryBankPostCode)
              ? formValues.intermediaryBankPostCode
              : null,
            country: !isEmpty(formValues?.intermediaryBankCountryCode)
              ? formValues.intermediaryBankCountryCode
              : null,
          },
        },
      }),
    }
  }

  const getRequestPayload = (
    contact: Contact,
    formValues: ContactFiatInstrumentFormData
  ): CreateContactBankAccountRequest => {
    const { currency, description, transferDestination, accountNumber, bankCode, routingNumber } =
      formValues ?? {}
    const bankDetails = getRequestBankPayload(formValues)
    const intermediaryBankDetails = getRequestIntermediaryBankPayload(formValues)

    return {
      type: InstrumentType.BANK_ACCOUNT,
      currency,
      description,
      destination: transferDestination as BeneficiaryTransferDestination,
      accountNumber: accountNumber?.trim(),
      bankCode: bankCode?.trim(),
      ...(routingNumber && { routingNumber: routingNumber?.trim() }),
      ...bankDetails,
      ...intermediaryBankDetails,
    }
  }

  const onSubmitBankAccount = () => {
    setCalloutError(null)
    const requestPayload = getRequestPayload(contact, formValues)

    submitBankAccount(
      { contactId: contact?.id, request: requestPayload },
      {
        onError: onErrorHandler,
      }
    )
  }

  const onErrorHandler = (error: BaseErrorResponse) => {
    if (error?.status === 428) {
      onSubmit2FARequest({
        challenge: error?.data?.challenge?.value,
        tokenResponse: formValues?.twoFactorCode || '',
        callbackUrl: error?.data?.callbackUrl,
      })
    } else if (error?.data?.errorList) {
      error.data.errorList.forEach(err => {
        form?.setError(err?.parameter as ContactFiatInstrumentFormKeys, { message: error?.message })
      })
    } else if (error?.status === 403) {
      form?.setError('twoFactorCode', {
        message: twoFactorEnabled ? t('authCodeInvalid') : t('authPasswordInvalid'),
      })
    } else {
      setCalloutError(t('createBankAccountError'))
      logSentryError('Error from ContactBankAccount - postCreateContactBankAccount', error, {
        contactId: contact?.id,
        currency: formValues?.currency,
        description: formValues?.description,
      })
    }
  }

  const onSubmit2FARequest = (payload: TwoFAAuthRequest) => {
    if (!payload?.callbackUrl) return

    submit2FA(payload, {
      onSuccess: () => {
        onSubmit2FASuccess?.()
        contactsAction?.onClose()
      },
      onError: onErrorHandler,
    })
  }

  return {
    isSubmittingBankAccount,
    submitBankAccountError,
    isSubmitting2FA,
    onSubmitBankAccount,
    calloutError,
    isLoading: isSubmittingBankAccount || isSubmitting2FA,
  }
}
